/**
 * #272 — exact-game open-order selection (pure) + the immutable confirmation
 * snapshot every bulk cancel is sent from.
 *
 * @ai-danger DESTRUCTIVE SCOPE. What this module returns as a `target` is what a
 * DELETE gets sent for. The display filter next to it (`kalshiOrderMatchesPanel`
 * in GameDetailPanel) may guess — it only decides whether a row is drawn, and a
 * wrong guess costs a misplaced row. This module may not guess: a wrong target
 * cancels a trader's resting order on a DIFFERENT game.
 *
 * The identity rule, therefore, is narrow and one-directional:
 *
 *   TARGET  ⇔  the order's market resolves, through marketStore's central
 *              fail-closed resolver, to the SAME five-part `canonicalGameKeyV2`
 *              as the selected game — and the order has a stable ID.
 *
 * Nothing else promotes to a target. Not a raw ticker match, not a team name,
 * not a legacy 4-seg key (it carries no game number, so promoting it silently
 * assumes game 1 and a doubleheader's nightcap would be swept by its opener's
 * button), not the panel's own book tickers, and not a cached Polymarket
 * description (session-persisted display data, resolved against a store state
 * that may no longer exist).
 *
 * Those weaker signals are still READ — but only in the other direction, to
 * decide whether the action must refuse. An order that can be tied to the
 * selected matchup yet cannot be assigned to the exact game is a BLOCKER: it
 * disables the whole per-game action and says why, because cancelling "the easy
 * subset" of a game silently leaves exposure the trader believes is gone. An
 * order with no evidence connecting it to the matchup is merely EXCLUDED — one
 * unclassifiable order elsewhere in the account must not disable every game.
 *
 * Consumed by GameDetailPanel (per-game scope) and PositionTracker (which
 * freezes its own all/visible scopes through `freezeCancelSnapshot`).
 */

import {
  baseGameKey,
  parseGameKeyV2,
  type GameKeyV2Parts,
} from '@/services/analyticsGameKey';
import { describePolyOrderCached } from '@/lib/polymarket/describeOrder';
import { useMarketStore } from '@/stores/marketStore';
import type { GameRow } from '@/views/ValueDashboard/types';
import type { KalshiOrder } from '@/types/kalshi';
import type { PolyRelayOpenOrder } from '@/lib/polymarket/relay';

export type CancelVenue = 'kalshi' | 'polymarket';

export interface OrderCancelTarget {
  /** `${venue}:${orderId}` — the dedup identity. Two venues can legitimately
   *  issue the same textual order ID; they are NOT the same order. */
  key: string;
  venue: CancelVenue;
  orderId: string;
}

export type GameOrderBlockReason =
  /** The selected panel's own key is not an exact five-part game key. */
  | 'invalid-target-game'
  /** The order ties to this matchup but its game number cannot be determined. */
  | 'ambiguous-game'
  /** The order is possibly related but its market frame never resolved. */
  | 'missing-market-metadata'
  /** A possibly related order arrived without a stable ID to cancel. */
  | 'missing-order-id';

export interface GameOrderBlocker {
  /** Null only for `invalid-target-game`, which is a property of the selected
   *  panel rather than of any one venue's order. */
  venue: CancelVenue | null;
  orderId: string | null;
  reason: GameOrderBlockReason;
  message: string;
}

export interface ExactGameOrderSelection {
  /** The suffix-stripped five-part key, or the raw base key when invalid. */
  gameKey: string;
  gameLabel: string;
  targets: readonly OrderCancelTarget[];
  blockers: readonly GameOrderBlocker[];
  venues: readonly CancelVenue[];
}

export interface FrozenCancelSnapshot {
  id: string;
  /** 'level' is #523's single ladder price level; the rest are #272's. */
  scope: 'game' | 'level' | 'visible' | 'all';
  label: string;
  createdAtMs: number;
  targets: readonly OrderCancelTarget[];
  venues: readonly CancelVenue[];
}

const VENUE_ORDER: readonly CancelVenue[] = ['kalshi', 'polymarket'];

function targetKeyFor(venue: CancelVenue, orderId: string): string {
  return `${venue}:${orderId}`;
}

/** Deterministic order: venue, then order ID. Confirmation copy, test
 *  assertions and the request sequence all read the same list. */
function sortTargets(targets: OrderCancelTarget[]): OrderCancelTarget[] {
  return [...targets].sort((a, b) => {
    const venueDelta = VENUE_ORDER.indexOf(a.venue) - VENUE_ORDER.indexOf(b.venue);
    if (venueDelta !== 0) return venueDelta;
    return a.orderId < b.orderId ? -1 : a.orderId > b.orderId ? 1 : 0;
  });
}

/** "Kalshi", "Polymarket", or "Kalshi and Polymarket". Every destructive
 *  confirmation must name each venue it is about to send a DELETE to, so this
 *  lives beside the selection rather than in one view. */
export function describeCancelVenues(venues: readonly CancelVenue[]): string {
  const names = venues.map((v) => (v === 'kalshi' ? 'Kalshi' : 'Polymarket'));
  if (names.length === 0) return 'none';
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** Exact per-venue count from the same frozen targets the confirmation sends. */
export function describeCancelVenueCounts(targets: readonly OrderCancelTarget[]): string {
  const parts = VENUE_ORDER.map((venue) => ({
    venue,
    count: targets.filter((target) => target.venue === venue).length,
  }))
    .filter(({ count }) => count > 0)
    .map(({ venue, count }) => `${venue === 'kalshi' ? 'Kalshi' : 'Polymarket'} ${count}`);
  return parts.length > 0 ? parts.join(' · ') : 'No orders';
}

function venuesOf(targets: readonly OrderCancelTarget[]): CancelVenue[] {
  return VENUE_ORDER.filter((venue) => targets.some((t) => t.venue === venue));
}

/**
 * True when a candidate key names the same MATCHUP as the target — same sport,
 * date and team pair — while deliberately IGNORING the game number.
 *
 * This is the blocker probe, never the target probe. Ignoring gameNo is exactly
 * what makes it unsafe as an identity test and exactly what makes it the right
 * ambiguity test: a legacy key that shares the matchup could be either game of a
 * doubleheader, which is the reason to refuse rather than the reason to act.
 */
function sharesMatchup(
  candidateKey: string | null | undefined,
  target: GameKeyV2Parts,
): boolean {
  const parsed = parseGameKeyV2(candidateKey);
  if (!parsed) return false;
  if (parsed.date !== target.date) return false;
  if (parsed.teamLo !== target.teamLo || parsed.teamHi !== target.teamHi) return false;
  // A sport-less (3-seg) analytics key is a wildcard on sport, not a mismatch.
  if (parsed.sport && target.sport && parsed.sport !== target.sport) return false;
  return true;
}

function firstNonEmptyString(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value === 'string' && value.trim() !== '') return value.trim();
  }
  return '';
}

/** Human label for a game, doubleheader-disambiguated. Falls back to team codes
 *  so a row that streamed before its display names still names its own game in
 *  the confirmation — an unnamed destructive confirmation is not confirmable. */
function buildGameLabel(game: GameRow, parts: GameKeyV2Parts | null): string {
  const away = firstNonEmptyString(game.awayName, game.awayCode) || '?';
  const home = firstNonEmptyString(game.homeName, game.homeCode) || '?';
  const date = parts?.date ?? game.date ?? '';
  const base = date ? `${away} @ ${home} — ${date}` : `${away} @ ${home}`;
  return parts && parts.gameNo > 1 ? `${base} (Game ${parts.gameNo})` : base;
}

function blockerMessage(
  reason: GameOrderBlockReason,
  venue: CancelVenue | null,
  orderId: string | null,
): string {
  const venueLabel = venue === 'polymarket' ? 'Polymarket' : venue === 'kalshi' ? 'Kalshi' : '';
  const who = orderId ? `${venueLabel} order ${orderId}` : `A ${venueLabel} order`;
  switch (reason) {
    case 'invalid-target-game':
      return 'This panel is not tied to a single identified game, so a per-game cancel cannot be scoped safely.';
    case 'ambiguous-game':
      return `${who} matches this matchup but its game could not be identified — on a doubleheader day it could belong to either game.`;
    case 'missing-market-metadata':
      return `${who} may belong to this game, but its market details have not loaded, so its game cannot be confirmed.`;
    case 'missing-order-id':
      return `${who} has no stable order ID, so it cannot be cancelled individually.`;
  }
}

/**
 * Every open order that provably belongs to `game`, plus the reasons the action
 * must refuse. Reads the CURRENT own-scoped order lists the caller already
 * renders — it never fetches, and never widens scope beyond what the trader can
 * see.
 */
export function selectExactGameOrders(args: {
  game: GameRow;
  kalshiOrders: readonly KalshiOrder[];
  polyOrders: readonly PolyRelayOpenOrder[];
}): ExactGameOrderSelection {
  const { game, kalshiOrders, polyOrders } = args;
  const store = useMarketStore.getState();

  const targetKey = baseGameKey(game.key);
  const targetParts = parseGameKeyV2(targetKey);
  // Five parts AND a sport: a 3-seg analytics key or a 4-seg legacy key parses
  // fine but names a matchup, not a game. Neither may scope a destructive sweep.
  const targetIsExact = targetParts?.sport != null && targetKey.split('|').length === 5;

  if (!targetIsExact || !targetParts) {
    return {
      gameKey: targetKey,
      gameLabel: buildGameLabel(game, targetParts),
      targets: Object.freeze([]),
      blockers: Object.freeze([
        {
          venue: null,
          orderId: null,
          reason: 'invalid-target-game' as const,
          message: blockerMessage('invalid-target-game', null, null),
        },
      ]),
      venues: Object.freeze([]),
    };
  }

  const targets: OrderCancelTarget[] = [];
  const blockers: GameOrderBlocker[] = [];
  const seen = new Set<string>();

  const addTarget = (venue: CancelVenue, orderId: string) => {
    const key = targetKeyFor(venue, orderId);
    if (seen.has(key)) return;
    seen.add(key);
    targets.push({ key, venue, orderId });
  };
  const addBlocker = (
    venue: CancelVenue,
    orderId: string | null,
    reason: GameOrderBlockReason,
  ) => {
    blockers.push({ venue, orderId, reason, message: blockerMessage(reason, venue, orderId) });
  };

  /** Shared tail for a row whose market DID resolve to this exact game. */
  const takeExact = (venue: CancelVenue, orderId: string) => {
    if (!orderId) {
      addBlocker(venue, null, 'missing-order-id');
      return;
    }
    addTarget(venue, orderId);
  };

  /** Shared tail for a row that is possibly related but not assignable. */
  const refuse = (venue: CancelVenue, orderId: string, reason: GameOrderBlockReason) => {
    addBlocker(venue, orderId || null, orderId ? reason : 'missing-order-id');
  };

  // ── Kalshi ────────────────────────────────────────────────────────
  // The panel's own away/home book tickers. The DISPLAY filter treats these as a
  // fallback when a frame is missing; the destructive path deliberately does not
  // inherit that — here they only mark a row as possibly related, which blocks.
  const panelTickers = new Set(
    [game.kalshi?.away?.marketTicker, game.kalshi?.home?.marketTicker].filter(
      (t): t is string => typeof t === 'string' && t !== '',
    ),
  );

  for (const order of kalshiOrders) {
    const ticker = typeof order.ticker === 'string' ? order.ticker : '';
    const orderId = firstNonEmptyString(order.orderId);
    const canon = ticker ? store.getGameKeyV2ForTicker(ticker) : null;

    if (canon === targetKey) {
      takeExact('kalshi', orderId);
      continue;
    }
    // A different exact game — proven unrelated, excluded without noise. This is
    // the doubleheader case too: game 2's stamped key is simply not game 1's.
    if (canon !== null) continue;

    // Unresolved. Decide only whether it is possibly THIS matchup.
    const market = ticker ? store.getMarket(ticker) : null;
    if (market && sharesMatchup(market.canonicalGameKey, targetParts)) {
      refuse('kalshi', orderId, 'ambiguous-game');
      continue;
    }
    if (panelTickers.has(ticker)) {
      refuse('kalshi', orderId, 'missing-market-metadata');
      continue;
    }
    // No frame, no matchup evidence, not one of this panel's books — this order
    // belongs to something else entirely. Excluding it is what keeps a stray
    // unresolved order from disabling the button on every game in the account.
  }

  // ── Polymarket ────────────────────────────────────────────────────
  // The panel's own Polymarket books, by raw identifier. Same role as
  // `panelTickers` on the Kalshi side and the same one-directional rule: these
  // mark an order as POSSIBLY RELATED (⇒ block), never as identified (⇒ target).
  // Without them, an order carrying this panel's own asset token is dropped
  // whenever the token→ticker map and the description cache are both cold, and
  // the sweep then cancels the rest of the game while that one keeps resting
  // (Brendan, PR #276 review).
  const panelPolyTokens = new Set<string>();
  const panelPolyConditions = new Set<string>();
  for (const book of [game.polymarket?.away, game.polymarket?.home]) {
    if (book?.tokenId) panelPolyTokens.add(book.tokenId);
    if (book?.noTokenId) panelPolyTokens.add(book.noTokenId);
    if (book?.conditionId) panelPolyConditions.add(book.conditionId);
  }
  const isThisPanelsPolyBook = (assetId: string, conditionId: string): boolean =>
    (assetId !== '' && panelPolyTokens.has(assetId)) ||
    (conditionId !== '' && panelPolyConditions.has(conditionId));

  for (const order of polyOrders) {
    const orderId = firstNonEmptyString(order.id, order.orderID, order.orderId);
    const assetId = typeof order.asset_id === 'string' ? order.asset_id : '';
    const conditionId = typeof order.market === 'string' ? order.market : '';

    // asset_id first (per-token, exact), then the market condition ID.
    const ticker =
      (assetId ? store.getTickerByTokenId(assetId) : null) ??
      (conditionId ? store.getTickerByConditionId(conditionId) : null);

    if (ticker) {
      const canon = store.getGameKeyV2ForTicker(ticker);
      if (canon === targetKey) {
        takeExact('polymarket', orderId);
        continue;
      }
      // A DIFFERENT exact key is proof of another game — proof outranks every
      // weaker signal below, including the panel's own identifiers.
      if (canon !== null) continue;
      const market = store.getMarket(ticker);
      if (market && sharesMatchup(market.canonicalGameKey, targetParts)) {
        refuse('polymarket', orderId, 'ambiguous-game');
        continue;
      }
      // Frame present but carrying no canonical key at all: fall through to the
      // panel-identifier check below rather than dropping the order.
      if (isThisPanelsPolyBook(assetId, conditionId)) {
        refuse('polymarket', orderId, 'missing-market-metadata');
      }
      continue;
    }

    // No live token/condition → ticker mapping at all.
    //
    // Two weaker signals remain, both blocker-only. The panel's own books are
    // the stronger of the two (a raw identifier match is visible right here);
    // the persisted describe cache is display-grade — resolved against a store
    // state that no longer exists and never re-validated.
    if (isThisPanelsPolyBook(assetId, conditionId)) {
      refuse('polymarket', orderId, 'missing-market-metadata');
      continue;
    }
    const cached = assetId ? describePolyOrderCached(assetId) : null;
    const cachedKey = cached?.canonicalGameKey;
    if (cachedKey && (cachedKey === targetKey || sharesMatchup(cachedKey, targetParts))) {
      refuse('polymarket', orderId, 'missing-market-metadata');
    }
  }

  const sorted = sortTargets(targets);
  return {
    gameKey: targetKey,
    gameLabel: buildGameLabel(game, targetParts),
    targets: Object.freeze(sorted),
    blockers: Object.freeze(blockers),
    venues: Object.freeze(venuesOf(sorted)),
  };
}

// Monotonic suffix so two snapshots frozen in the same millisecond are still
// distinguishable — the UI keys a running report by snapshot id.
let _snapshotSeq = 0;

/**
 * Freeze a destructive set at the instant the confirmation opens.
 *
 * The array is COPIED and frozen, not referenced. The live order lists behind a
 * selection keep polling; without the copy, an order that appears between the
 * click and the confirm would be added to a set the trader already read and
 * approved. The modal's count, venue list and IDs must all read from the value
 * returned here, never from a live array.
 */
export function freezeCancelSnapshot<S extends FrozenCancelSnapshot['scope']>(args: {
  scope: S;
  label: string;
  targets: readonly OrderCancelTarget[];
  nowMs?: number;
  // Generic in `scope` only so the literal survives into the return type: it is
  // what lets the Cancel All audit refuse a #523 level snapshot at compile time
  // (see bulkCancelAttemptAudit). No runtime effect.
}): FrozenCancelSnapshot & { scope: S } {
  const createdAtMs = args.nowMs ?? Date.now();
  const targets = Object.freeze(sortTargets([...args.targets]).map((t) => Object.freeze({ ...t })));
  return Object.freeze({
    id: `${args.scope}-${createdAtMs}-${++_snapshotSeq}`,
    scope: args.scope,
    label: args.label,
    createdAtMs,
    targets,
    venues: Object.freeze(venuesOf(targets)),
  });
}
